import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { Squiggle, FloatingBlob } from "@/components/Squiggle";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-10%" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function Manifest() {
  usePageMeta({
    title: "Manifest · LevelLeven",
    description:
      "Soms begint de weg vooruit niet bij werk. Waarom LevelLeven begint bij de mens, bij vandaag, bij wat er werkelijk speelt.",
  });

  return (
    <div className="bg-ll-cream text-ll-ink">
      {/* HERO — rustige editorial opening */}
      <section className="pt-20 sm:pt-32 pb-16 relative overflow-hidden">
        <FloatingBlob color="#F0B98D" size={180} className="top-10 -right-20 opacity-50" />
        <FloatingBlob color="#97B79D" size={120} className="bottom-0 -left-10 opacity-40" delay={1.2} />
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10">
          <motion.p {...fadeUp} className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">
            Manifest
          </motion.p>
          <motion.h1
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="mt-6 font-heading text-4xl sm:text-5xl lg:text-7xl leading-[1.02] tracking-tight font-light"
          >
            Soms begint de weg vooruit{" "}
            <span className="italic text-ll-coral">niet bij werk</span>.
          </motion.h1>
        </div>
      </section>

      {/* BODY — lange rustige leeservaring */}
      <article className="pb-24 sm:pb-32">
        <div className="max-w-[720px] mx-auto px-6 sm:px-10 space-y-10 text-lg sm:text-xl text-ll-ink leading-[1.6] font-light">

          <motion.p {...fadeUp}>
            Wij geloven dat niemand eerst 'klaar' hoeft te zijn om te mogen beginnen.
          </motion.p>

          <motion.ul {...fadeUp} className="space-y-2 pl-0 text-ll-navy/90 italic">
            <li>Niet eerst alles op orde.</li>
            <li>Niet eerst weten waar je naartoe moet.</li>
            <li>Niet eerst voldoen aan wat een ander van je verwacht.</li>
          </motion.ul>

          <motion.p {...fadeUp} className="font-heading text-3xl sm:text-4xl italic text-ll-coral leading-tight">
            Soms is beginnen al genoeg.
          </motion.p>

          <motion.p {...fadeUp}>
            Want stilstand betekent niet dat iemand niets kan. Soms betekent het dat er te veel tegelijk speelt. Stress. Gezondheid. Eenzaamheid. Gebrek aan ritme. Gedoe thuis. Teleurstellingen die zich opstapelen. Of simpelweg het gevoel dat je niet meer weet waar je moet beginnen.
          </motion.p>

          <motion.p {...fadeUp}>
            LevelLeven begint daarom niet met een cv, een vacature of een vastomlijnd plan.
          </motion.p>

          <motion.div {...fadeUp} className="py-2">
            <p className="font-heading text-3xl sm:text-4xl leading-tight">Wij beginnen bij de mens.</p>
            <p className="font-heading text-3xl sm:text-4xl leading-tight italic text-ll-coral">Bij vandaag.</p>
            <p className="font-heading text-3xl sm:text-4xl leading-tight">Bij wat er werkelijk speelt.</p>
          </motion.div>

          <motion.p {...fadeUp}>
            Niet om iemand te repareren. Wel om samen te begrijpen waarom beweging moeilijk is geworden. Wat energie kost. Wat ruimte geeft. Wie kan helpen. En waar misschien al iets kleins mogelijk is.
          </motion.p>

          <motion.p {...fadeUp} className="font-heading text-2xl sm:text-3xl italic text-ll-navy/90 leading-snug">
            Want een stap hoeft niet groot te zijn om betekenis te hebben.
          </motion.p>

          <motion.ul {...fadeUp} className="space-y-2 text-ll-navy/90">
            <li>Weer ergens naartoe gaan.</li>
            <li>Een afspraak nakomen.</li>
            <li>Beter slapen.</li>
            <li>Iemand bellen.</li>
            <li>Iets proberen wat je lang hebt uitgesteld.</li>
            <li>Ontdekken dat je ergens plezier in hebt.</li>
          </motion.ul>

          <motion.p {...fadeUp} className="font-heading text-4xl italic text-ll-coral">
            Dat telt.
          </motion.p>

          <motion.p {...fadeUp}>
            Wij geloven dat ontwikkeling niet ontstaat doordat iemand één keer wordt verteld wat hij moet doen. Ontwikkeling groeit door begrijpen, proberen, ervaren en opnieuw proberen. Soms gaat dat vooruit. Soms even terug. Dat hoort erbij.
          </motion.p>

          <motion.div {...fadeUp} className="py-2">
            <p className="font-heading text-3xl sm:text-4xl leading-tight">Plezier en zin zijn daarin geen luxe.</p>
            <p className="mt-2 font-heading text-3xl sm:text-4xl italic text-ll-coral leading-tight">Ze kunnen juist het begin zijn.</p>
          </motion.div>

          <motion.p {...fadeUp}>
            Een moment waarop iemand weer nieuwsgierig wordt. Een plek waar vertrouwen terugkomt. Een ervaring waardoor iemand denkt: misschien kan er toch iets veranderen.
          </motion.p>

          <motion.p {...fadeUp}>
            En verandering zit nooit alleen in één persoon.
          </motion.p>

          <motion.p {...fadeUp}>
            Ook de mensen om je heen doen ertoe. De omgeving. De mogelijkheden die je krijgt. De regels waar je tegenaan loopt. De ruimte die een school, gemeente, werkgever of andere organisatie bereid is te maken.
          </motion.p>

          <motion.p {...fadeUp}>
            Daarom kijken wij verder dan een dossier, een probleem of 'afstand tot de arbeidsmarkt'.
          </motion.p>

          <motion.p {...fadeUp} className="font-heading text-3xl sm:text-4xl italic text-ll-coral leading-tight">
            Een mens is altijd meer dan dat.
          </motion.p>

          <motion.p {...fadeUp}>
            Wij geloven ook niet dat iedereen dezelfde route moet lopen of hetzelfde tempo moet hebben. Wat voor de één een logische volgende stap is, kan voor een ander vandaag nog te groot zijn.
          </motion.p>

          <motion.div {...fadeUp} className="py-2">
            <p className="font-heading text-3xl sm:text-4xl leading-tight">Dus niet eerst weten waar je eindigt.</p>
            <p className="mt-2 font-heading text-3xl sm:text-4xl italic text-ll-coral leading-tight">Wel ontdekken wat vandaag mogelijk is.</p>
          </motion.div>

          <motion.p {...fadeUp}>
            Uiteindelijk gaat LevelLeven over steeds iets meer regie. Over weer keuzes durven maken. Over ervaren dat je zelf invloed hebt op wat er hierna gebeurt.
          </motion.p>

          <motion.ul {...fadeUp} className="space-y-2 text-ll-navy/90">
            <li>Misschien leidt dat naar werk.</li>
            <li>Misschien naar opleiding.</li>
            <li>Misschien naar participatie.</li>
            <li>En soms begint het veel kleiner.</li>
          </motion.ul>

          <motion.ul {...fadeUp} className="pl-6 border-l-2 border-ll-coral/50 space-y-1 italic text-ll-ink">
            <li>Met één gesprek.</li>
            <li>Eén ervaring.</li>
            <li>Eén besluit.</li>
            <li>Eén vonk.</li>
          </motion.ul>
        </div>
      </article>

      {/* VONK-MOMENT · gele oplichtende bal */}
      <section className="pb-24 sm:pb-32 relative overflow-hidden">
        <div className="max-w-[720px] mx-auto px-6 sm:px-10 text-center">
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto w-28 h-28 rounded-full flex items-center justify-center"
            style={{
              background: "radial-gradient(circle at 30% 30%, #F0B98D 0%, #E06B3F 85%)",
              boxShadow: "0 0 60px #F0B98D88",
            }}
          >
            <motion.div
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <Sparkles size={32} strokeWidth={1.5} className="text-ll-ink" />
            </motion.div>
          </motion.div>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.3 }}
            className="mt-12 font-heading text-3xl sm:text-5xl leading-[1.08] italic text-ll-ink"
          >
            Want beweging hoeft niet spectaculair te beginnen.
          </motion.p>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.5 }}
            className="mt-4 font-heading text-3xl sm:text-5xl leading-[1.08] text-ll-coral"
          >
            Ze hoeft alleen maar te beginnen.
          </motion.p>

          <div className="mt-16 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/voor-jou"
              data-testid="manifest-cta-deelnemer"
              className="group inline-flex items-center gap-3 bg-ll-ink text-ll-cream hover:bg-ll-coral rounded-full pl-7 pr-2 py-2 transition-colors"
            >
              <span className="text-base font-medium">Kijk wat we doen</span>
              <span className="bg-ll-coral group-hover:bg-ll-cream group-hover:text-ll-ink text-ll-cream w-10 h-10 rounded-full flex items-center justify-center transition-colors">
                <ArrowUpRight size={16} />
              </span>
            </Link>
            <Link
              to="/vonkvinder"
              className="inline-flex items-center gap-2 border border-ll-ink text-ll-ink hover:bg-ll-ink hover:text-ll-cream rounded-full px-6 py-3 transition-colors"
            >
              Vind jouw vonk
            </Link>
          </div>
        </div>

        <Squiggle color="#F0B98D" className="absolute bottom-10 left-1/2 -translate-x-1/2 w-40 h-10 opacity-70" delay={1.4} />
      </section>
    </div>
  );
}
